import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, EmptyState, PageHeader, Spinner, StatusPill } from '../components/ui';
import { ClassesAPI } from '../services/endpoints/admin';
import { useDispatchAction } from '../hooks/useDispatchAction';
import type { Collection } from '../types/api';
import { formatDate } from '../utils/format';
import { NotImplementedBanner } from './UsersPage';
import './common.css';

export function ClassesPage() {
  const [items, setItems] = useState<Collection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const nav = useNavigate();
  const { run, busy } = useDispatchAction();

  const toggleDisabled = async (c: Collection) => {
    const next = !c.deleted;
    const updated = await run(next ? 'Disable class' : 'Enable class', () => ClassesAPI.setDisabled(c.id, next));
    if (updated) setItems((prev) => prev.map((it) => (it.id === c.id ? { ...it, deleted: next } : it)));
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await ClassesAPI.list();
      setItems(Array.isArray(data) ? data : []);
      setError(false);
    } catch {
      setError(true);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <PageHeader
        eyebrow="04 — CLASSES"
        title={<>The <em>marquee</em>.</>}
        lede="Every creator class. Open one to manage its episodes, disable or re-enable it, and feature it on the app's “All” tab — only one class is featured at a time."
      />

      {error && <NotImplementedBanner endpoint="/api/admin/collections" />}

      {loading && !items.length ? <Card><Spinner /> Loading…</Card> :
       items.length === 0 ? <Card><EmptyState title="No classes yet" hint="Creators’ classes will appear here once published." /></Card> :
        <Card padded={false}>
          <table className="nb-table">
            <thead>
              <tr><th>Class</th><th>Creator</th><th>Type</th><th>Episodes</th><th>Created</th><th>Status</th><th /></tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} onClick={() => nav(`/classes/${c.id}`)} role="link" style={{ cursor: 'pointer' }}>
                  <td>
                    <div className="nb-table__primary">
                      {c.title || c.name}
                      {c.featured && <> <StatusPill tone="moss">Featured</StatusPill></>}
                    </div>
                    {c.description && (
                      <div className="nb-table__sub">{c.description.slice(0, 80)}{c.description.length > 80 ? '…' : ''}</div>
                    )}
                  </td>
                  <td className="nb-table__mono">{c.creatorName || '—'}</td>
                  <td>{c.type === 'AUDIO' ? 'Audio' : 'Video'}</td>
                  <td className="nb-table__mono">{c.count ?? 0}</td>
                  <td className="nb-table__mono">{formatDate(c.createdTimestamp)}</td>
                  <td>{c.deleted
                    ? <StatusPill tone="rust">Disabled</StatusPill>
                    : <StatusPill tone="moss">Active</StatusPill>}</td>
                  <td style={{ textAlign: 'right' }}>
                    <Button
                      size="sm"
                      tone={c.deleted ? 'ember' : 'ghost'}
                      disabled={busy}
                      onClick={(e) => { e.stopPropagation(); toggleDisabled(c); }}
                    >
                      {c.deleted ? 'Enable' : 'Disable'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      }
    </div>
  );
}
